// front/src/preview-mode/graph/entities.ts
import type { PreviewDestination, PreviewEventDef } from "./types";

export const EVENT_DEF_IDS = {
  page_view: "a8f7c2b1-1e60-4a3b-9f10-00001103c001",
  click: "a8f7c2b1-1e60-4a3b-9f10-00001103c002",
  form_started: "a8f7c2b1-1e60-4a3b-9f10-00001103c003",
  form_submitted: "a8f7c2b1-1e60-4a3b-9f10-00001103c004",
  appointment_booking_started: "a8f7c2b1-1e60-4a3b-9f10-00001103c005",
  appointment_booking_completed: "a8f7c2b1-1e60-4a3b-9f10-00001103c006",
  call_initiated: "a8f7c2b1-1e60-4a3b-9f10-00001103c007",
} as const;

export const EVENT_DEFS: PreviewEventDef[] = [
  {
    eventDefId: EVENT_DEF_IDS.page_view,
    name: "Page View",
    funnelStage: "",
    isRecommended: false,
    isOther: false,
    isHidden: false,
    description: "User viewed a page",
    totalEventsDelivered: 0,
    isHighVolumeEvent: true,
    adPlatformsDelivered: 0,
    adPlatformsDeliveredList: "",
    isSpecialCaseAdsDelivPv: true,
    hasNoRecentTraffic: false,
    tags: { "Auto Event": true },
    type: "",
  },
  {
    eventDefId: EVENT_DEF_IDS.click,
    name: "Click",
    funnelStage: "",
    isRecommended: false,
    isOther: true,
    isHidden: false,
    description: "User clicked an element",
    totalEventsDelivered: 0,
    isHighVolumeEvent: true,
    adPlatformsDelivered: 0,
    adPlatformsDeliveredList: "",
    isSpecialCaseAdsDelivPv: false,
    hasNoRecentTraffic: false,
    tags: { "Auto Event": true },
    type: "",
  },
  {
    eventDefId: EVENT_DEF_IDS.form_started,
    name: "Form Started",
    funnelStage: "MidFunnel",
    isRecommended: true,
    isOther: false,
    isHidden: false,
    description: "User focused a form field",
    totalEventsDelivered: 0,
    isHighVolumeEvent: false,
    adPlatformsDelivered: 3,
    adPlatformsDeliveredList: "",
    isSpecialCaseAdsDelivPv: false,
    hasNoRecentTraffic: false,
    tags: { "Custom Event": true },
    type: "",
  },
  {
    eventDefId: EVENT_DEF_IDS.form_submitted,
    name: "Form Submitted",
    funnelStage: "MidFunnel",
    isRecommended: true,
    isOther: false,
    isHidden: false,
    description: "User submitted a form",
    totalEventsDelivered: 0,
    isHighVolumeEvent: false,
    adPlatformsDelivered: 3,
    adPlatformsDeliveredList: "",
    isSpecialCaseAdsDelivPv: false,
    hasNoRecentTraffic: false,
    tags: { "Custom Event": true },
    type: "",
  },
  {
    eventDefId: EVENT_DEF_IDS.appointment_booking_started,
    name: "Appointment Booking Started",
    funnelStage: "MidFunnel",
    isRecommended: true,
    isOther: false,
    isHidden: false,
    description: "User entered the appointment booking flow",
    totalEventsDelivered: 0,
    isHighVolumeEvent: false,
    adPlatformsDelivered: 4,
    adPlatformsDeliveredList: "",
    isSpecialCaseAdsDelivPv: false,
    hasNoRecentTraffic: false,
    tags: { "Custom Event": true },
    type: "",
  },
  {
    eventDefId: EVENT_DEF_IDS.appointment_booking_completed,
    name: "Appointment Booking Completed",
    funnelStage: "BottomFunnel",
    isRecommended: true,
    isOther: false,
    isHidden: false,
    description: "User completed an appointment booking",
    totalEventsDelivered: 0,
    isHighVolumeEvent: false,
    adPlatformsDelivered: 5,
    adPlatformsDeliveredList: "",
    isSpecialCaseAdsDelivPv: false,
    hasNoRecentTraffic: false,
    tags: { "Custom Event": true, "Conversion Event": true },
    type: "",
  },
  {
    eventDefId: EVENT_DEF_IDS.call_initiated,
    name: "Call Initiated",
    funnelStage: "BottomFunnel",
    isRecommended: true,
    isOther: false,
    isHidden: false,
    description: "User clicked a phone number link",
    totalEventsDelivered: 0,
    isHighVolumeEvent: false,
    adPlatformsDelivered: 2,
    adPlatformsDeliveredList: "",
    isSpecialCaseAdsDelivPv: false,
    hasNoRecentTraffic: false,
    tags: { "Custom Event": true, "Conversion Event": true },
    type: "",
  },
];

// `key` values are real catalog destination keys (see destinations.tsx) so
// slug/category/logo/detail-link all resolve. Spread across categories so the
// overview's category tabs aren't all "Search & social".
export const DESTINATIONS: PreviewDestination[] = [
  // Search & social — delivery metrics, no impressions.
  {
    id: "dest-google-ads",
    key: "Google AdWords New",
    name: "Google Ads",
    isEnabled: true,
    hasDeliveryMetrics: true,
    hasImpressions: false,
    settings: { accountId: "AW-481516234" },
  },
  {
    id: "dest-meta-ads",
    key: "Facebook Pixel",
    name: "Facebook Pixel",
    isEnabled: true,
    hasDeliveryMetrics: true,
    hasImpressions: false,
    settings: { pixelId: "612340987651234" },
  },
  {
    id: "dest-bing-ads",
    key: "Bing Ads",
    name: "Microsoft Advertising",
    isEnabled: true,
    hasDeliveryMetrics: true,
    hasImpressions: false,
    settings: { tagId: "97461023", builtInPageViewEnabled: true },
  },
  {
    id: "dest-ga4",
    key: "Google Analytics 4",
    name: "Google Analytics 4",
    isEnabled: true,
    hasDeliveryMetrics: true,
    hasImpressions: false,
    settings: { measurementId: "G-PREVIEW123", apiKey: "preview-api-secret" },
  },
  // Display / DSP — the only destinations that accrue impressions.
  {
    id: "dest-ttd",
    key: "theTradeDesk",
    name: "The Trade Desk",
    isEnabled: true,
    hasDeliveryMetrics: true,
    hasImpressions: true,
    settings: { advertiserId: "preview-advertiser" },
  },
  // Audiences — syncs cohorts, so no delivery metrics or impressions.
  {
    id: "dest-google-ads-audiences",
    key: "Google Ads Audiences",
    name: "Google Ads Audiences",
    isEnabled: true,
    hasDeliveryMetrics: false,
    hasImpressions: false,
    settings: { customerId: "123-456-7890" },
  },
];
